'use client';
import { Body } from '../typo/body';
import { Heading } from '../typo/heading';
import { Small } from '../typo/small';

import React from 'react';
import { useCollapse } from 'react-collapsed';

/**
 * Card component
 * Simple UI card container
 */

type PropsType = {
  heading?: string | null;
  sup?: string | null;
  small?: string | React.ReactNode | null;
  body?: string | null;
  className?: string;
  noPadding?: boolean;
  collapse?: boolean;
  defaultExpanded?: boolean;
  onSwitch?: () => void | undefined;
  checked?: boolean;
  separator?: boolean;
  children: React.ReactNode;
  id?: string;
  style?: React.CSSProperties;
  hover?: boolean;
  'data-test-id'?: string;
};

export const Card = ({
  ref,
  heading = null,
  sup = null,
  small = null,
  body = null,
  className = '',
  noPadding = false,
  hover = false,
  collapse = false,
  defaultExpanded = true,
  onSwitch,
  checked,
  separator,
  children,
  style,
  ...attrs
}: PropsType & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse({
    defaultExpanded: defaultExpanded,
  });
  const tw = {
    card: `bg-surface-2 rounded-lg border border-surface-2-stroke ${
      noPadding ? '' : ' p-4 lg:p-6'
    } ${!hover ? '' : 'hover:bg-choice-list-item-hover'} ${className} `,
    content_wrapper: 'flex flex-col gap-y-4',
  };

  return (
    <div className={tw.card} ref={ref} style={style} {...attrs}>
      {(heading || small || body) && (
        <div className={tw.content_wrapper}>
          {heading && (
            <Heading
              isExpanded={collapse ? isExpanded : undefined}
              getToggleProps={collapse ? { ...getToggleProps() } : {}}
              onSwitch={onSwitch}
              checked={checked}
              separator={separator}
              sup={sup}
            >
              {heading}
            </Heading>
          )}
          {small && <Small>{small}</Small>}
          {body && <Body>{body}</Body>}
        </div>
      )}
      {collapse ? (
        <div {...getCollapseProps()}>{children}</div>
      ) : (
        <>{children}</>
      )}
    </div>
  );
};

Card.displayName = 'Card';
